import { useCallback, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router";
import { create } from "zustand";
import { useAuthStore } from "@/stores/authStore";
import { useWorkspaceStore } from "@/stores/workspaceStore";

/** The link opened for someone other than the signed-in user. */
export type DeepLinkMismatch = {
  /** The link as opened, params included — where to land after signing in again. */
  target: string;
  signedInAs: string;
};

const LINK_PARAMS = ["orgId", "brandId", "expectedUserId"] as const;

/** Outlives the URL params it came from: they are stripped the moment it is set. */
const useMismatchStore = create<{ mismatch: DeepLinkMismatch | null }>(() => ({ mismatch: null }));

/**
 * A link into the app can name its workspace and reader:
 * `?orgId=<organizationId>&brandId=<brandId>&expectedUserId=<userId>`.
 * The emails a brand's guests get (a month to review, a strategy sent to them)
 * carry all three, so the page opens on the right org and brand even when the
 * reader last had another one selected.
 *
 * Applied once the user (and their memberships) is known, then stripped from
 * the URL; an org the user doesn't belong to is ignored. `expectedUserId` is a
 * courtesy check, not security (the server checks access as always): when the
 * signed-in user is someone else, `mismatch` is set so the shell can say the
 * link wasn't meant for this account.
 *
 * `pending` is true until the params are applied, so the shell can hold
 * org-dependent decisions (the guest gate, redirects) until the switch lands.
 */
export function useWorkspaceDeepLink(): {
  pending: boolean;
  mismatch: DeepLinkMismatch | null;
  dismissMismatch: () => void;
} {
  const [params, setParams] = useSearchParams();
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const orgs = user?.userOrganizations;
  const hydrated = useWorkspaceStore((s) => s._hasHydrated);
  const mismatch = useMismatchStore((s) => s.mismatch);

  const orgId = params.get("orgId");
  const brandId = params.get("brandId");
  const expectedUserId = params.get("expectedUserId");
  const pending = !!(orgId || brandId || expectedUserId);

  useEffect(() => {
    if (!pending || !user || !orgs || !hydrated) return;
    const store = useWorkspaceStore.getState();
    const isMember = !!orgId && orgs.some((uo) => uo.organization.id === orgId);
    if (isMember && store.selectedOrganizationId !== orgId) {
      store.setSelectedOrganizationId(orgId);
    }
    if (brandId && (isMember || !orgId)) store.setSelectedBrandId(brandId);

    if (expectedUserId && expectedUserId !== user.id) {
      useMismatchStore.setState({
        mismatch: { target: `${pathname}?${params.toString()}`, signedInAs: user.email },
      });
    }
    const rest = new URLSearchParams(params);
    for (const key of LINK_PARAMS) rest.delete(key);
    setParams(rest, { replace: true });
  }, [pending, user, orgs, hydrated, orgId, brandId, expectedUserId, params, pathname, setParams]);

  const dismissMismatch = useCallback(() => useMismatchStore.setState({ mismatch: null }), []);
  return { pending, mismatch, dismissMismatch };
}
